// DealDetailPage — детальная страница сделки с actions (edit + next contact + cross-entity tabs).
// R34 / U-12: полноценное редактирование через DealEditDialog (PUT + expectedUpdatedAt).
// R34 / U-1: кнопка «Назад» (navigate(-1)).
// U-19 (sled-V9 W5): смена стадии — DealStagePath над табами, без модалки;
// кнопка «Изменить стадию» удалена (постановка «вместо изменения стадии»).
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  Alert,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { ConfirmDialog } from '@/theme/ConfirmDialog';
import { DealStageChip } from './components/DealStageChip';
import { DealStagePath } from './components/DealStagePath';
import { AmountChip } from './components/AmountChip';
import { CrossEntityTabs, type CrossEntityTab } from './components/CrossEntityTabs';
import { DealComments } from './components/DealComments';
import { DealEditDialog } from './components/DealEditDialog';
import { NextContactDialog } from './components/NextContactDialog';
import { PaymentBlock } from '@/components/PaymentBlock';
// V14 W6: блок счетов сделки (сводка Итого/Оплачено/Остаток + счета, §3.8).
import { InvoiceBlock } from '@/components/InvoiceBlock';
import { EntityEmailDialog } from '@/pages/email/components/EntityEmailDialog';
import { dealsApi, type Deal, type DealStage } from '@/api/deals';
import { emailApi } from '@/api/email';
import { tasksApi } from '@/api/tasks';
import { TaskStatusChip } from '@/pages/tasks/components/TaskStatusChip';
import { TaskPriorityChip } from '@/pages/tasks/components/TaskPriorityChip';
import { contactsApi } from '@/api/contacts';
import { companiesApi } from '@/api/companies';
import { useUserMap, resolveUserName } from '@/api/users';
import { extractApiErrorMessage } from '@/lib/apiError';
import { formatDate, formatDateOnly } from '@/lib/format';
import { STAGE_TRANSITIONS } from '@/lib/dealStages';
import { t } from '@/i18n';

/** Порог «скорого» контакта: меньше суток до nextContactAt → warning-подсветка (мс). */
const NEXT_CONTACT_WARNING_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/**
 * Рендерит блок «Следующий контакт»: «—» при null; при контакте ближе 24ч —
 * warning-подсветка (data-warning), канон NextContactCell из DealsListPage (U-18).
 */
function NextContactBlock({ deal }: { deal: Deal }): JSX.Element {
  const diffMs = deal.nextContactAt
    ? new Date(deal.nextContactAt).getTime() - Date.now()
    : null;
  const warning = diffMs != null && diffMs >= 0 && diffMs < NEXT_CONTACT_WARNING_THRESHOLD_MS;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {t('crm.deals.nextContactLabel')}
      </Typography>
      <Typography
        variant="body2"
        color={warning ? 'warning.main' : 'text.primary'}
        data-testid="deal-next-contact"
        data-warning={warning ? 'true' : undefined}
      >
        {formatDate(deal.nextContactAt)}
      </Typography>
    </Box>
  );
}

/** Детальная страница сделки с cross-entity вкладками. */
export function DealDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [nextContactOpen, setNextContactOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const query = useQuery({
    queryKey: ['deal', id],
    queryFn: () => dealsApi.get(id ?? ''),
    enabled: Boolean(id),
  });

  // R24 / D22: резолв владельца по id (R23 useUserMap) — admin за пределами первой сотни не теряется.
  const ownerUserId = query.data?.ownerUserId ?? null;
  const userMap = useUserMap(ownerUserId ? [ownerUserId] : []);

  // U-19 (sled-V9 W5): без замыкания на newStage-стейт — next приходит из onSelect
  // (DealStagePath), диалог смены стадии удалён.
  const stageMutation = useMutation({
    mutationFn: (next: DealStage) => {
      // B2: optimistic-lock требует фактический updatedAt из данных — без fallback «now».
      if (!query.data) throw new Error('Данные сделки не загружены');
      return dealsApi.changeStage(id ?? '', {
        newStage: next,
        expectedUpdatedAt: query.data.updatedAt,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['deal', id] });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
    onError: () => {
      // B2: 409/ошибка → refetch — свежий updatedAt, пользователь не в тупике повторных 409.
      void queryClient.invalidateQueries({ queryKey: ['deal', id] });
    },
  });

  // B1.3: удаление сделки — DELETE с optimistic-lock (expectedUpdatedAt из данных).
  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!query.data) throw new Error('Данные сделки не загружены');
      return dealsApi.delete(query.data.id, query.data.updatedAt);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      void queryClient.invalidateQueries({ queryKey: ['deal', id] });
      navigate('/crm/deals');
    },
    onError: () => {
      // B2: после 409 — refetch, повторная попытка удаления с актуальным updatedAt.
      void queryClient.invalidateQueries({ queryKey: ['deal', id] });
    },
  });

  // Cross-entity запросы (lazy — только если открыта соответствующая вкладка).
  const contactId = query.data?.contactId ?? null;
  const companyId = query.data?.companyId ?? null;

  const contactQuery = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => contactsApi.get(contactId ?? ''),
    enabled: Boolean(contactId),
  });

  const companyQuery = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companiesApi.get(companyId ?? ''),
    enabled: Boolean(companyId) && activeTab === 'company',
  });

  // R38b / U-9: таб «Задачи» — список задач, связанных со сделкой (?dealId=).
  const dealTasksQuery = useQuery({
    queryKey: ['tasks', 'deal', id],
    queryFn: () => tasksApi.list({ dealId: id ?? '', page: 0, size: 100 }),
    enabled: activeTab === 'tasks',
  });

  if (query.isLoading) return <LoadingSpinner label="Загрузка сделки…" />;
  if (query.isError || !query.data) {
    return (
      <EmptyState
        title="Сделка не найдена"
        description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        actionLabel="К списку сделок"
        onAction={() => navigate('/crm/deals')}
      />
    );
  }
  const deal: Deal = query.data;
  const recipientEmail = contactQuery.data?.email?.trim() ?? '';
  const canSendEmail = Boolean(recipientEmail);
  const sendEmailButton = (
    <Button variant="outlined" disabled={!canSendEmail} onClick={() => setEmailDialogOpen(true)}>
      {t('email.send.title')}
    </Button>
  );

  const tabs: CrossEntityTab[] = [
    { id: 'overview', label: 'Обзор' },
    { id: 'comments', label: 'Комментарии' },
    // R38b / U-9: таб связанных задач — всегда доступен (даже если задач 0).
    { id: 'tasks', label: t('crm.deals.tasksTab') },
    // V12 U-35-C: платежи сделки (блок PaymentBlock в отдельной вкладке).
    { id: 'payments', label: t('payments.title') },
    ...(contactId ? [{ id: 'contact', label: 'Контакт' }] : []),
    ...(companyId ? [{ id: 'company', label: 'Компания' }] : []),
  ];

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 1 }}
        aria-label={t('common.back')}
      >
        {t('common.back')}
      </Button>
      <PageHeader
        title={deal.title}
        subtitle={`Создана ${formatDate(deal.createdAt)}`}
        actions={
          <Stack direction="row" spacing={1}>
            {canSendEmail ? (
              sendEmailButton
            ) : (
              <Tooltip title={t('email.send.noRecipientDeal')}>
                <span>{sendEmailButton}</span>
              </Tooltip>
            )}
            {/* R34 / U-12: полноценное редактирование через DealEditDialog. */}
            <Button variant="outlined" onClick={() => setEditDialogOpen(true)}>
              {t('common.edit')}
            </Button>
            {/* R35 / U-5: quick-action из карточки сделки (переиспользованный диалог R21). */}
            <Button variant="outlined" onClick={() => setNextContactOpen(true)}>
              {t('crm.deals.nextContact')}
            </Button>
            {/* B1.3: удаление сделки (soft delete + optimistic lock, FR-D6). */}
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              {t('common.delete')}
            </Button>
          </Stack>
        }
      />
      {/* Ошибки мутаций: смена стадии (stage-path, вне диалога) и удаление — в общем Alert. */}
      {stageMutation.isError || deleteMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteMutation.isError
            ? extractApiErrorMessage(deleteMutation.error, 'Не удалось удалить сделку')
            : extractApiErrorMessage(stageMutation.error, 'Не удалось сменить стадию')}
        </Alert>
      ) : null}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <DealStageChip stage={deal.stage} size="medium" />
              <AmountChip amount={deal.amount} currency={deal.currency} size="medium" />
            </Stack>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Владелец
                </Typography>
                <Typography variant="body2">{resolveUserName(userMap, deal.ownerUserId)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Ожидаемая дата закрытия
                </Typography>
                <Typography variant="body2">{formatDateOnly(deal.dueDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Дата закрытия
                </Typography>
                <Typography variant="body2">{formatDate(deal.closedAt)}</Typography>
              </Box>
              {/* U-18 (sled-V9): окно контактов — следующий (warning при <24ч) и последний. */}
              <NextContactBlock deal={deal} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('crm.deals.lastContactLabel')}
                </Typography>
                <Typography variant="body2" data-testid="deal-last-contact">
                  {formatDate(deal.lastContactAt)}
                </Typography>
              </Box>
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Описание
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {deal.description ?? '—'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ mb: 2 }}>
        {/* U-19 (sled-V9 W5): видимый путь стадий — смена без модалки,
            недоступные стадии серые (disabled), клик → POST /stage. */}
        <DealStagePath
          stage={deal.stage}
          transitions={STAGE_TRANSITIONS[deal.stage]}
          onSelect={(next) => stageMutation.mutate(next)}
          pending={stageMutation.isPending}
        />
      </Box>
      <CrossEntityTabs tabs={tabs} activeId={activeTab} onChange={setActiveTab} />

      <Box sx={{ pt: 3 }}>
        {activeTab === 'overview' ? (
          <Typography variant="body2" color="text.secondary">
            Версия: {deal.version} · Создано: {formatDate(deal.createdAt)}
          </Typography>
        ) : null}
        {activeTab === 'comments' ? <DealComments dealId={deal.id} /> : null}
        {activeTab === 'payments' ? (
          <Stack spacing={4}>
            {/* V14 W6: счета сделки — сводка и счета (вкладка «Платежи», §3.8). */}
            <InvoiceBlock dealId={deal.id} dealCurrency={deal.currency} />
            <PaymentBlock entityType="DEAL" entityId={deal.id} />
          </Stack>
        ) : null}
        {activeTab === 'tasks' ? (
          <Box>
            {/* R38b / U-9: создание задачи со связью — prefill dealId через query. */}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate(`/tasks/new?dealId=${deal.id}`)}>
              {t('crm.deals.createTask')}
            </Button>
            {dealTasksQuery.isLoading ? (
              <Box sx={{ mt: 2 }}>
                <LoadingSpinner label="Загрузка задач…" />
              </Box>
            ) : !dealTasksQuery.data || dealTasksQuery.data.content.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t('crm.deals.noTasks')}
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 2 }}>
                {dealTasksQuery.data.content.map((task) => (
                  <Card
                    key={task.id}
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                          {task.title}
                        </Typography>
                        <TaskStatusChip status={task.status} />
                        <TaskPriorityChip priority={task.priority} />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        ) : null}
        {activeTab === 'contact' && contactId ? (
          contactQuery.isLoading ? (
            <LoadingSpinner />
          ) : contactQuery.data ? (
            <Card>
              <CardContent>
                <Typography variant="h6">
                  {contactQuery.data.firstName} {contactQuery.data.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {contactQuery.data.email ?? '—'} · {contactQuery.data.phone ?? '—'}
                </Typography>
              </CardContent>
            </Card>
          ) : null
        ) : null}
        {activeTab === 'company' && companyId ? (
          companyQuery.isLoading ? (
            <LoadingSpinner />
          ) : companyQuery.data ? (
            <Card>
              <CardContent>
                <Typography variant="h6">{companyQuery.data.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  ИНН: {companyQuery.data.taxId ?? '—'}
                </Typography>
              </CardContent>
            </Card>
          ) : null
        ) : null}
      </Box>

      {emailDialogOpen && canSendEmail ? (
        <EntityEmailDialog
          open
          onClose={() => setEmailDialogOpen(false)}
          recipientEmail={recipientEmail}
          preview={(templateId) => emailApi.previewTemplate({ templateId, dealId: deal.id })}
          send={(payload) => dealsApi.sendEmail(deal.id, payload)}
        />
      ) : null}

      {/* R34 / U-12: диалог монтируется заново при каждом открытии — свежие defaultValues. */}
      {editDialogOpen ? (
        <DealEditDialog deal={deal} open onClose={() => setEditDialogOpen(false)} />
      ) : null}

      {/* R35 / U-5: quick-action — создаёт событие календаря, привязанное к сделке. */}
      {nextContactOpen ? (
        <NextContactDialog deal={deal} open onClose={() => setNextContactOpen(false)} />
      ) : null}

      {/* B1.3: подтверждение удаления сделки (soft delete). */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Удалить сделку?"
        message="Сделка будет помечена как удалённая. Действие необратимо."
        confirmLabel={t('common.delete')}
        confirmColor="error"
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
